/**
 * Browser-only SVG → PNG rasterization for map export (#167).
 * Keeps `mapToSvg` pure; this module is the DOM edge.
 */

import { isNothing, toMaybe } from "./maybe";

export async function rasterizeSvgToPngBytes(
  svg: string,
  width: number,
  height: number,
): Promise<Uint8Array> {
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    const context = toMaybe(canvas.getContext("2d"));
    if (isNothing(context)) {
      throw new Error("Could not create canvas context for map PNG export");
    }
    context.value.drawImage(img, 0, 0, canvas.width, canvas.height);
    const pngBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((value) => {
        if (value === null) reject(new Error("PNG encode failed"));
        else resolve(value);
      }, "image/png");
    });
    return new Uint8Array(await pngBlob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error("Failed to decode SVG for PNG export"));
    img.src = src;
  });
}
